import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api.ts";
import placeholder from "../assets/Car place holder PNG.png";
import Pagination from "../components/common/Pagination.jsx";
import { useLanguage } from "../context/LanguageContext";

const Blog = () => {
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const response = await api.get(
          `/blogs/paginated?page=${currentPage}&limit=12&orderBy=id&order=DESC`
        );

        if (response.data && response.data.success) {
          if (
            response.data.data &&
            response.data.data.data &&
            Array.isArray(response.data.data.data)
          ) {
            setBlogs(response.data.data.data);
            setTotalPages(response.data.data.pages || 1);
            setTotal(
              response.data.data.total || response.data.data.data.length
            );
          } else if (response.data.data && Array.isArray(response.data.data)) {
            setBlogs(response.data.data);
            setTotalPages(response.data.pages || 1);
            setTotal(response.data.total || response.data.data.length);
          } else if (Array.isArray(response.data)) {
            setBlogs(response.data);
            setTotalPages(1);
            setTotal(response.data.length);
          } else {
            setBlogs([]);
            setTotalPages(1);
            setTotal(0);
          }
        } else {
          setBlogs([]);
          setTotalPages(1);
          setTotal(0);
        }
      } catch (error) {
        console.error("Error fetching blogs:", error);
        setBlogs([]);
        setTotalPages(1);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getExcerpt = (htmlString) => {
    if (!htmlString) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  if (loading) {
    return (
      <div
        className='min-h-screen flex items-center justify-center'
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto'></div>
          <p className='mt-4 text-gray-600'>{translate("blogPage.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className='min-h-screen bg-white'
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Blog Header */}
      <div className='relative bg-[#010101BF] overflow-hidden'>
        {/* Background Geometric Shapes */}
        <div className='absolute inset-0'>
          {/* Left side shapes */}
          <div className='absolute left-10 top-20 w-16 h-16 bg-white/10 rounded-full'></div>
          <div className='absolute left-20 top-32 w-8 h-8 bg-[#E95923]/20 transform rotate-45'></div>
          <div className='absolute left-32 top-16 w-12 h-12 border-2 border-white/20 rounded-full'></div>

          {/* Right side shapes */}
          <div className='absolute right-20 top-16 w-20 h-4 bg-[#E95923]/30'></div>
          <div className='absolute right-10 top-28 w-16 h-4 bg-white/20'></div>
          <div className='absolute right-32 top-24 w-2 h-16 bg-white/30'></div>
          <div className='absolute right-16 top-40 w-2 h-12 bg-[#E95923]/40'></div>
        </div>

        <div className='relative z-10 max-w-7xl mx-auto px-4 py-8 md:py-12'>
          <div className='text-center'>
            <h1 className='text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight'>
              {translate("blogPage.heading")}
            </h1>
            <p className='text-lg md:text-xl text-gray-300 max-w-3xl mx-auto'>
              {translate("blogPage.subtitle")}
            </p>
          </div>
        </div>
      </div>

      {/* Blog Posts Section */}
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16'>
        {blogs && blogs.length > 0 ? (
          <>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
              {blogs.map((blog) => (
                <article
                  key={blog.id}
                  className='group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2'
                >
                  {/* Blog Image */}
                  <div className='relative h-56 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden'>
                    <img
                      src={blog.featured_image || placeholder}
                      alt={blog.title}
                      className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-300'
                    />
                    <div className='absolute inset-0 bg-gradient-to-t from-black/20 to-transparent'></div>
                  </div>

                  {/* Blog Content */}
                  <div className='p-6'>
                    <div
                      className={`flex items-center text-sm text-gray-500 mb-3 ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <span>
                        {new Date(blog.createdAt).toLocaleDateString(
                          isRTL ? "ar-AE" : "en-US"
                        )}
                      </span>
                      <span className='mx-2'>•</span>
                      <span>{blog.author?.name}</span>
                    </div>

                    <h3 className='text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors'>
                      {blog.title}
                    </h3>

                    <p className='text-gray-600 text-sm mb-6 line-clamp-3 leading-relaxed'>
                      {getExcerpt(blog.description)}
                    </p>

                    <Link
                      to={`/blog/${blog.slug}`}
                      className={`inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold text-sm transition-colors ${
                        isRTL
                          ? "group-hover:-translate-x-1 flex-row-reverse"
                          : "group-hover:translate-x-1"
                      }`}
                    >
                      {translate("blogPage.readMore")}
                      <svg
                        className={`w-4 h-4 transition-transform ${
                          isRTL
                            ? "mr-2 group-hover:-translate-x-1 rotate-180"
                            : "ml-2 group-hover:translate-x-1"
                        }`}
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M9 5l7 7-7 7'
                        />
                      </svg>
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                setCurrentPage={setCurrentPage}
              />
            )}
          </>
        ) : (
          <div className='text-center py-20'>
            <div className='w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center'>
              <svg
                className='w-12 h-12 text-gray-400'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                />
              </svg>
            </div>
            <h3 className='text-2xl font-bold text-gray-900 mb-4'>
              {translate("blogPage.noArticles")}
            </h3>
            <p className='text-gray-600 max-w-md mx-auto'>
              {translate("blogPage.noArticlesMessage")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
